<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="modalstack-overlay modal fade show"
      :class="overlayClass"
      role="dialog"
      aria-modal="true"
      @click.self="() => onClose(c.props?.hasCloseCallback || null)"
    >
      <div
        class="modalstack-overlay__card modal-dialog"
        :class="classDialog"
        @click.stop
      >
        <div class="modal-content modalstack-overlay__content">
          <header v-if="!c.props?.hideHeader" class="modal-header modalstack-overlay__head">
            <template v-if="c.props?.headerText">
              <h1 class="fs-4 fw-normal mb-0">{{ c.props.headerText }}</h1>
            </template>
            <select
              v-if="c.props?.options"
              class="form-select form-select-sm ms-4 w-auto"
              @change="onSelectChange"
            >
              <option
                v-for="(opt, index) in c.props.options"
                :key="index"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <button
              type="button"
              class="btn-close ms-auto"
              :disabled="closeDisabled"
              :aria-label="t('common.close')"
              @click="() => onClose(c.props?.hasCloseCallback || null)"
            ></button>
          </header>
          <div
            class="modal-body modalstack-overlay__body"
            :class="classBody"
          >
            <Ctxprovider
              :provide="c.provide"
              :key="c.id"
            >
              <component
                :is="c.component"
                v-bind="c.props"
                :ref="bindComponentRef"
                v-on="c.events"
              />
            </Ctxprovider>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { provide, ref, computed, watch, shallowRef, toValue, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { syncRef } from '@vueuse/core'
import { useModalStack } from '@/stores/modalstack'
import { useUiStore } from '@/stores/ui'
import { lockPageScroll, resetPageScrollLock } from '@/lib/pageScrollLock'
import Ctxprovider from './ctxprovider.vue'

const { t } = useI18n()
const modalStack = useModalStack()
const { stack } = storeToRefs(modalStack)
const { fullscreen } = storeToRefs(useUiStore())
const isOpen = ref(false)
const c = shallowRef()
const fs = ref(false)

const overlayClass = computed(() => {
  const parts = []
  if (c.value?.props?.class) {
    parts.push(c.value.props.class)
  }
  if (isHistoryModal.value) {
    parts.push('modalstack-overlay--history')
  }
  return parts.filter(Boolean).join(' ')
})

const isHistoryModal = computed(() =>
  String(c.value?.props?.class || '').includes('history-stack-modal')
)

const classDialog = computed(() => {
  const size = c.value?.props?.size
  return {
    'fit-content': c.value?.props?.fitContent,
    'modal-dialog-centered': !fs.value,
    'modal-fullscreen': fs.value,
    'modal-xl': size === 'xl',
    'modal-lg': size === 'lg',
    'modal-sm': size === 'sm',
  }
})
const classBody = computed(() => ({ 'px-1': fs.value }))
const closeDisabled = computed(() =>
  isLoading.value || Boolean(toValue(c.value?.props?.pending))
)

syncRef(fs, fullscreen, { direction: 'rtl' })

watch(isOpen, open => {
  if (open) {
    lockPageScroll()
    document.body.classList.add('modalstack-open')
    if (isHistoryModal.value) {
      document.body.classList.add('history-modal-open')
    }
  } else {
    resetPageScrollLock()
    document.body.classList.remove('modalstack-open')
    document.body.classList.remove('history-modal-open')
  }
})

watch(
  stack,
  stack => {
    isOpen.value = stack.length > 0
    let _c = stack[stack.length - 1]
    if (!_c) {
      c.value = undefined
      document.body.classList.remove('history-modal-open')
      return
    }

    const s = modalStack.stateGet(_c.id)
    if (s) {
      Object.assign(_c.props, s)
    }
    if (_c.provide) {
      for (const [k, v] of Object.entries(_c.provide)) {
        provide(k, toValue(v))
      }
    }
    c.value = _c
    if (String(_c.props?.class || '').includes('history-stack-modal')) {
      document.body.classList.add('history-modal-open')
    } else {
      document.body.classList.remove('history-modal-open')
    }
  },
  { deep: true }
)

provide('fullscreen', fs)

function onClose(data = null) {
  window.dispatchEvent(new CustomEvent('modalstack:close'))
  fs.value = fullscreen.value
  modalStack.pop(data)
}

function onSelectChange(e) {
  const value = e.target.value
  c.value?.props?.onSelect?.(value)
}

function bindComponentRef(el) {
  if (c.value?.componentRef) {
    c.value.componentRef.value = el
  }
}

/** Back / уход со страницы — снять модалку и размытие.
 * Не закрывать стек, пока поверх архива открыт docviewer (его history.back
 * раньше сносил и архив).
 */
function closeStackOnNavigate() {
  if (stack.value.length === 0) {
    return
  }
  if (
    document.body.classList.contains('nested-docviewer-active') ||
    document.body.classList.contains('history-docviewer-active')
  ) {
    return
  }
  modalStack.closeAll()
  window.dispatchEvent(new CustomEvent('modalstack:close'))
  fs.value = fullscreen.value
}

onMounted(() => {
  window.addEventListener('popstate', closeStackOnNavigate)
  window.addEventListener('pagehide', closeStackOnNavigate)
})

onUnmounted(() => {
  window.removeEventListener('popstate', closeStackOnNavigate)
  window.removeEventListener('pagehide', closeStackOnNavigate)
  document.body.classList.remove('modalstack-open')
  document.body.classList.remove('history-modal-open')
})

provide('modalstack', true)

const isLoading = ref(false)
provide('setModalLoading', loading => (isLoading.value = loading))
</script>
